import React from 'react';

const pillBase = {
  padding: '8px 14px',
  border: '1px solid #dee2e6',
  borderRadius: '100px',
  backgroundColor: 'white',
  fontFamily: '"Baikal Book"',
  fontSize: '13px',
  color: '#666',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap',
  minWidth: '40px',
};

const pillActive = {
  backgroundColor: '#00C8FF',
  borderColor: '#00C8FF',
  color: '#000',
  fontWeight: 600,
};

const pillDisabled = {
  cursor: 'not-allowed',
  opacity: 0.4,
};

// Returns an array like [1, '…', 4, 5, 6, '…', 14] for centered windowing
function buildPageWindow(current, total, around = 1) {
  if (total <= 7 + around * 2) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current]);
  for (let i = 1; i <= around; i++) {
    pages.add(current - i);
    pages.add(current + i);
  }
  const sorted = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('…');
    out.push(sorted[i]);
  }
  return out;
}

const Pagination = ({ page, totalItems, pageSize, onPageChange, position = 'bottom', scrollOnChange = true }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalItems);

  if (totalItems <= pageSize) return null;

  const goto = (target) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== safePage) {
      onPageChange(next);
      if (scrollOnChange) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const windowed = buildPageWindow(safePage, totalPages, 2);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '14px 18px',
        backgroundColor: '#f9fafb',
        ...(position === 'top'
          ? { borderBottom: '1px solid #e5e7eb' }
          : { borderTop: '1px solid #e5e7eb' }),
        flexWrap: 'wrap',
      }}
    >
      <div style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
        Showing <strong>{startItem.toLocaleString()}</strong>–<strong>{endItem.toLocaleString()}</strong> of{' '}
        <strong>{totalItems.toLocaleString()}</strong>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => goto(safePage - 1)}
          disabled={safePage <= 1}
          style={{ ...pillBase, ...(safePage <= 1 ? pillDisabled : {}) }}
        >
          ← Prev
        </button>
        {windowed.map((p, idx) =>
          p === '…' ? (
            <span
              key={`gap-${idx}`}
              style={{
                fontFamily: '"Baikal Book"',
                fontSize: '13px',
                color: '#9ca3af',
                padding: '0 4px',
              }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => goto(p)}
              style={{ ...pillBase, ...(p === safePage ? pillActive : {}) }}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => goto(safePage + 1)}
          disabled={safePage >= totalPages}
          style={{ ...pillBase, ...(safePage >= totalPages ? pillDisabled : {}) }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default Pagination;
